import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Plus,
  Copy,
  RotateCcw,
  Trash2,
  Pencil,
  History,
  Loader2,
  Gift,
  Ticket,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const ACTION_META = {
  create:    { label: 'Creada',    color: 'bg-green-600',  icon: Plus },
  duplicate: { label: 'Duplicada', color: 'bg-blue-600',   icon: Copy },
  reset:     { label: 'Reiniciada',color: 'bg-orange-600', icon: RotateCcw },
  delete:    { label: 'Eliminada', color: 'bg-red-600',    icon: Trash2 },
  update:    { label: 'Editada',   color: 'bg-purple-600', icon: Pencil },
};

const ENTITY_META = {
  raffle: { label: 'Rifa',   icon: Ticket },
  sorteo: { label: 'Sorteo', icon: Gift },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const relativeFromNow = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'hace un momento';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} d`;
  const mo = Math.floor(d / 30);
  return `hace ${mo} mes${mo > 1 ? 'es' : ''}`;
};

const ActivityHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEntity !== 'all') params.append('entity_type', filterEntity);
      if (filterAction !== 'all') params.append('action', filterAction);
      params.append('limit', '200');
      const response = await axios.get(`${API}/activity-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [filterEntity, filterAction]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClearAll = async () => {
    if (!window.confirm('¿Borrar todo el historial? Esta acción no se puede deshacer.')) return;
    try {
      const response = await axios.delete(`${API}/activity-logs`);
      toast.success(`Se borraron ${response.data.deleted} registros`);
      fetchLogs();
    } catch (error) {
      toast.error('Error al borrar historial');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <History className="text-purple-600" />
            Historial de actividad
          </h2>
          <p className="text-gray-600 text-sm">
            Todo lo que pasó con tus rifas y sorteos en orden cronológico.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleClearAll}
          disabled={logs.length === 0}
          className="text-red-600 hover:bg-red-50"
          data-testid="clear-history-btn"
        >
          <Trash2 size={16} className="mr-2" />
          Borrar historial
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[160px]">
              <label className="text-sm text-gray-600 flex items-center gap-1 mb-1">
                <Filter size={14} /> Tipo
              </label>
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger data-testid="filter-entity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="raffle">Solo rifas</SelectItem>
                  <SelectItem value="sorteo">Solo sorteos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-sm text-gray-600 mb-1 block">Acción</label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger data-testid="filter-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="create">Crear</SelectItem>
                  <SelectItem value="duplicate">Duplicar</SelectItem>
                  <SelectItem value="reset">Reiniciar</SelectItem>
                  <SelectItem value="update">Editar</SelectItem>
                  <SelectItem value="delete">Eliminar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-gray-500 ml-auto">
              {loading ? 'Cargando...' : `${logs.length} ${logs.length === 1 ? 'evento' : 'eventos'}`}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Eventos</CardTitle>
          <CardDescription>Más recientes primero</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto mb-2" size={32} />
              <p className="text-gray-500">Cargando historial...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <History size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay eventos para mostrar</p>
              <p className="text-sm">A medida que crees, dupliques o reinicies rifas y sorteos, aparecerán acá.</p>
            </div>
          ) : (
            <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5">
              {logs.map((log) => {
                const actionMeta = ACTION_META[log.action] || { label: log.action, color: 'bg-gray-500', icon: History };
                const entityMeta = ENTITY_META[log.entity_type] || { label: log.entity_type, icon: History };
                const ActionIcon = actionMeta.icon;
                const EntityIcon = entityMeta.icon;
                return (
                  <li key={log.id} className="ml-6" data-testid={`log-item-${log.action}`}>
                    <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${actionMeta.color} text-white ring-4 ring-white`}>
                      <ActionIcon size={12} />
                    </span>
                    <div className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className={`${actionMeta.color} text-white`}>{actionMeta.label}</Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <EntityIcon size={12} />
                          {entityMeta.label}
                        </Badge>
                        <span className="text-sm font-semibold text-gray-800 truncate">
                          {log.entity_title}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
                        <span title={formatDate(log.created_at)}>{relativeFromNow(log.created_at)}</span>
                        <span>· {formatDate(log.created_at)}</span>
                        {log.admin_email && <span>· {log.admin_email}</span>}
                      </div>
                      {log.action === 'duplicate' && log.details?.source_title && (
                        <p className="text-xs text-gray-600 mt-1">
                          Copiada desde: <strong>{log.details.source_title}</strong>
                        </p>
                      )}
                      {log.action === 'update' && Array.isArray(log.details?.fields) && log.details.fields.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          Campos: {log.details.fields.join(', ')}
                        </p>
                      )}
                      {log.action === 'reset' && log.details?.total_numbers !== undefined && (
                        <p className="text-xs text-gray-600 mt-1">
                          {log.details.total_numbers} números regenerados
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityHistory;
